/**
 * Admin Stores client helpers.
 * BFF `/server/stores*` requires SLAS Bearer (same as Google proxy / GG-009).
 */

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useAccessToken} from '@salesforce/commerce-sdk-react';

import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url';

import {SHOWROOM_COUNTRY_TO_SITE_ID} from '@amblique/king-living/app/constants/site-config';

export const ADMIN_STORES_STALE_TIME_MS = 15 * 60 * 1000;

export const adminStoreByIdQueryKey = (siteId, storeId) => ['admin-store', siteId, storeId];

// Absolute URL — Node fetch rejects relative paths during SSR.
const toServerUrl = (path) => `${getAppOrigin()}${path}`;

const adminByIdInflight = new Map();
const adminByCountriesInflight = new Map();

const toAdminCountriesKey = (countryCodes) =>
  [...new Set((countryCodes || []).filter(Boolean))].sort().join(',');

const shareInflight = (map, key, create) => {
  const existing = map.get(key);

  if (existing) return existing;

  const request = create().finally(() => map.delete(key));

  map.set(key, request);

  return request;
};

export const fetchAdminStoresByCountries = async (countryCodes, getTokenWhenReady) => {
  const key = toAdminCountriesKey(countryCodes);

  if (!key || !getTokenWhenReady) return {countries: {}};

  return shareInflight(adminByCountriesInflight, key, async () => {
    const accessToken = await getTokenWhenReady();
    const response = await fetch(
      toServerUrl(`/server/stores?${new URLSearchParams({countries: key})}`),
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response.ok ? response.json() : {countries: {}};
  });
};

export const searchAdminStores = async (body, getTokenWhenReady) => {
  if (!getTokenWhenReady) {
    return {siteId: body?.siteId, total: 0, data: []};
  }

  const accessToken = await getTokenWhenReady();
  const response = await fetch(toServerUrl('/server/stores/search'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  return response.ok ? response.json() : {siteId: body?.siteId, total: 0, data: []};
};

/**
 * Fetch one store via Admin BFF (MS-004 by id).
 * Used by: dynamic-page (page validate), showroom-details (cross-site detail).
 */
export const fetchAdminStoreById = async ({id, siteId, countryCode, getTokenWhenReady}) => {
  if (!id || !siteId || !getTokenWhenReady) return null;

  const code = String(countryCode || '').trim().toUpperCase();

  return shareInflight(adminByIdInflight, `${siteId}:${id}:${code}`, async () => {
    const accessToken = await getTokenWhenReady();
    const params = new URLSearchParams({id, siteId});

    if (SHOWROOM_COUNTRY_TO_SITE_ID[code]) params.set('countries', code);

    const response = await fetch(toServerUrl(`/server/stores?${params}`), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) return null;

    const buckets = (await response.json())?.countries || {};
    const data = buckets[Object.keys(buckets)[0]]?.data;

    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  });
};

/**
 * Load Admin stores for non-current country tabs (MS-004 list).
 * Used by: stores listing page (`/showrooms`).
 */
export const useMultiSiteStores = (countryCodes, options = {}) => {
  const {enabled = true} = options;
  const {getTokenWhenReady} = useAccessToken();
  const codes = useMemo(
    () => [...new Set((countryCodes || []).filter(Boolean))].sort(),
    [countryCodes]
  );

  const query = useQuery({
    queryKey: ['admin-stores', 'countries', codes],
    queryFn: () => fetchAdminStoresByCountries(codes, getTokenWhenReady),
    enabled: enabled && codes.length > 0,
    staleTime: ADMIN_STORES_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    byCountry: query.data?.countries || {},
    isLoading: query.isLoading,
  };
};
